<template>
  <div class="fixed inset-0 top-auto flex rounded-lg bg-white p-2 text-gray-dark shadow-card lg:static lg:mb-6 lg:block lg:p-6">
    <div class="mb-4 w-1/2 text-center lg:w-auto lg:text-left">
      <div v-if="venue.low_rate">
        <span class="font-bw-gradual text-xl font-bold lg:text-3xl">{{ $f.currency(venue.low_rate, currency) }}</span>
        <span class="hidden lg:inline-block">/night</span>
      </div>
      <div v-else class="font-medium">
        Check price
      </div>
      <div class="text-sm lg:hidden">
        on Booking.com
      </div>
    </div>

    <div v-if="!['sm', 'md'].includes($screen.name)" class="mb-6 hidden lg:block xl:block">
      <Dropdown v-model="showDates" class="block-important" class-prop="lg:w-100 right-0 left-auto" mobile-title="Dates">
        <template #button>
          <button
            ref="button"
            class="block w-full rounded border border-gray-mid2 px-3 py-2 text-left"
            @click="showDates = !showDates"
          >
            <span v-if="datesRange">
              <span v-if="datesRange.startDate">{{ $f.date(datesRange.startDate) }}</span>
              <span v-else>Check in</span>
              /
              <span v-if="datesRange.endDate">{{ $f.date(datesRange.endDate) }}</span>
              <span v-else>Check out</span>
            </span>
          </button>
        </template>
        <ClientOnly>
          <DateRangePicker v-model="datesRange" />
        </ClientOnly>
      </Dropdown>
    </div>
    <div v-if="!['sm', 'md'].includes($screen.name)" class="mb-16 hidden lg:block xl:block">
      <Dropdown v-model="showRooms" class="block-important" class-prop="lg:w-96 right-0 left-auto" mobile-title="Guests">
        <template #button>
          <button
            ref="button"
            class="block w-full rounded border border-gray-mid2 px-3 py-2 text-left"
            @click="showRooms = !showRooms"
          >
            <span>
              {{ numGuests }} {{ $f.pluralize('guest', numGuests) }}
              •
              {{ numRooms }} {{ $f.pluralize('room', numRooms) }}
            </span>
          </button>
        </template>
        <GuestsRoomsSelectors
          v-model:num-guests="numGuests"
          v-model:num-rooms="numRooms"
          :max-guests="maxGuests"
          :min-guests="minGuests"
          :max-rooms="maxRooms"
          :min-rooms="minRooms"
        />
        <template #footer>
          <div class="flex justify-between">
            <button class="letter-sp15 h-10 rounded-md bg-blue px-4 text-sm uppercase leading-10 text-white hover:bg-blue-dark md:px-8" @click="showRooms = false">
              Update
            </button>
          </div>
        </template>
      </Dropdown>
    </div>

    <div class="mb-2 hidden text-center lg:block">
      <a
        :href="makeBookingUrl()"
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg
          class="inline-block"
          width="238"
          height="24"
          viewBox="0 0 238 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M106.223 1.82753C106.223 1.34283 106.416 0.877989 106.759 0.535257C107.102 0.192525 107.568 -2.0504e-05 108.053 -2.0504e-05C108.538 -2.0504e-05 109.004 0.192525 109.347 0.535257C109.69 0.877989 109.883 1.34283 109.883 1.82753C109.865 2.30042 109.665 2.74806 109.324 3.07638C108.982 3.4047 108.527 3.58813 108.053 3.58813C107.579 3.58813 107.124 3.4047 106.783 3.07638C106.441 2.74806 106.241 2.30042 106.223 1.82753Z" fill="#273B7D" />
          <path d="M135.095 14.0157C135.095 13.0014 135.913 12.1818 136.921 12.1818C137.408 12.1818 137.875 12.3749 138.219 12.7186C138.563 13.0623 138.756 13.5284 138.756 14.0145C138.756 14.5005 138.563 14.9667 138.219 15.3103C137.875 15.654 137.408 15.8471 136.921 15.8471C135.915 15.8471 135.095 15.0262 135.095 14.0145" fill="#499FDD" />
          <path d="M76.1925 13.223C74.6175 13.223 73.5215 11.9732 73.5215 10.1864C73.5215 8.40085 74.6174 7.15236 76.1937 7.15236C77.7777 7.15236 78.8851 8.40085 78.8851 10.1864C78.8851 12.0012 77.802 13.223 76.1925 13.223ZM76.1925 4.48358C72.8589 4.48358 70.439 6.88256 70.439 10.1864C70.439 13.4915 72.8602 15.8892 76.1925 15.8892C79.5401 15.8892 81.9689 13.4915 81.9689 10.1877C81.9689 6.88256 79.5401 4.48358 76.1937 4.48358H76.1925ZM102.68 10.4728C102.564 10.2486 102.418 10.0418 102.244 9.85805L102.143 9.75242L102.25 9.65061C102.403 9.48898 102.559 9.29808 102.71 9.07663L105.653 4.70884H102.08L99.8692 8.12595C99.7443 8.30922 99.492 8.40085 99.1135 8.40085H98.6102V1.94079C98.6102 0.649034 97.8036 0.473406 96.9332 0.473406H95.4422L95.446 15.7212H98.6114V11.1473H98.9084C99.269 11.1473 99.5136 11.1893 99.627 11.3853L101.373 14.6764C101.861 15.5698 102.346 15.7212 103.261 15.7212H105.686L103.879 12.7381L102.682 10.4728H102.68ZM118.046 4.45813C116.435 4.45813 115.408 5.17336 114.832 5.77788L114.641 5.97133L114.572 5.70789C114.404 5.0601 113.833 4.70502 112.975 4.70502H111.557L111.566 15.7174H114.71V10.642C114.71 10.1457 114.773 9.71551 114.906 9.32226C115.255 8.13232 116.231 7.3929 117.451 7.3929C118.432 7.3929 118.816 7.91087 118.816 9.24717V14.0439C118.816 15.1842 119.344 15.7174 120.488 15.7174H121.984L121.979 8.71265C121.979 5.9306 120.619 4.4594 118.046 4.4594V4.45813ZM108.092 4.70884H106.603L106.613 13.2243V15.7212H108.202L108.258 15.7238L109 15.7212H109.736V15.7174H109.741L109.748 6.3824C109.748 5.25609 109.209 4.71011 108.094 4.71011L108.092 4.70884ZM88.5393 13.223C86.9642 13.223 85.867 11.9732 85.867 10.1864C85.867 8.40085 86.9643 7.15236 88.5406 7.15236C90.1207 7.15236 91.2319 8.40085 91.2319 10.1864C91.2319 12.0012 90.1488 13.223 88.5406 13.223H88.5393ZM88.5393 4.48358C85.2031 4.48358 82.7819 6.88256 82.7819 10.1864C82.7819 13.4915 85.2032 15.8892 88.5406 15.8892C91.8831 15.8892 94.317 13.4915 94.317 10.1877C94.317 6.88256 91.8831 4.48358 88.5406 4.48358" fill="#273B7D" />
          <path d="M155.918 13.223C154.343 13.223 153.245 11.9732 153.245 10.1864C153.245 8.40085 154.343 7.15236 155.918 7.15236C157.501 7.15236 158.611 8.40085 158.611 10.1864C158.611 12.0012 157.528 13.223 155.918 13.223ZM155.918 4.48358C152.582 4.48358 150.162 6.88256 150.162 10.1864C150.162 13.4915 152.582 15.8892 155.918 15.8892C159.262 15.8892 161.695 13.4915 161.695 10.1877C161.695 6.88256 159.261 4.48358 155.918 4.48358Z" fill="#499FDD" />
          <path d="M128.605 12.7381C126.886 12.7381 126.274 11.2414 126.274 9.83769C126.274 9.21918 126.431 7.20455 128.441 7.20455C129.438 7.20455 130.767 7.48962 130.767 9.94078C130.767 12.2532 129.591 12.7381 128.605 12.7381ZM132.4 4.67958C131.803 4.67958 131.345 4.91756 131.114 5.35154L131.027 5.51954L130.882 5.39227C130.375 4.95447 129.465 4.43395 127.988 4.43395C125.048 4.43395 123.069 6.63948 123.069 9.91915C123.069 13.1975 125.117 15.4883 128.047 15.4883C129.047 15.4883 129.838 15.2542 130.465 14.7807L130.708 14.5987V14.9042C130.708 16.3754 129.756 17.1873 128.029 17.1873C127.189 17.1873 126.426 16.9837 125.914 16.7979C125.248 16.5968 124.856 16.7635 124.587 17.4317L124.337 18.0477L123.984 18.9487L124.202 19.0645C125.308 19.65 126.747 19.9999 128.047 19.9999C130.723 19.9999 133.85 18.6318 133.85 14.7807L133.861 4.67958H132.4Z" fill="#273B7D" />
          <path d="M64.4859 13.0969L61.9271 13.0944V10.04C61.9271 9.38583 62.1807 9.0473 62.7401 8.9684H64.4859C65.7309 8.9684 66.5362 9.75236 66.5375 11.0212C66.5362 12.3244 65.7513 13.0957 64.4859 13.0957V13.0969ZM61.9271 4.85514V4.05081C61.9271 3.34703 62.2253 3.01232 62.879 2.96905H64.189C65.3117 2.96905 65.9845 3.63974 65.9845 4.76351C65.9845 5.61874 65.5232 6.61778 64.2298 6.61778H61.9271V4.85514ZM67.7533 7.90445L67.2906 7.64482L67.6946 7.29993C68.1648 6.8965 68.9511 5.98909 68.9511 4.42371C68.9511 2.026 67.0906 0.478437 64.2094 0.478437H60.547C60.1349 0.493443 59.7444 0.666222 59.4564 0.960967C59.1684 1.25571 59.005 1.64983 59 2.06163V15.6664H64.2743C67.4767 15.6664 69.5436 13.9254 69.5436 11.2287C69.5436 9.77654 68.8759 8.53569 67.7533 7.90445Z" fill="#273B7D" />
          <path d="M175.736 4.49502C175.087 4.49741 174.447 4.64537 173.863 4.92799C173.279 5.21061 172.766 5.62066 172.362 6.12785L172.135 6.41547L171.956 6.09349C171.373 5.03336 170.371 4.49502 168.978 4.49502C167.517 4.49502 166.537 5.30952 166.082 5.79314L165.784 6.11512L165.669 5.69132C165.503 5.08044 164.959 4.74573 164.136 4.74573H162.815L162.802 15.7161H165.803V10.8736C165.803 10.4486 165.856 10.0298 165.962 9.59078C166.249 8.41992 167.036 7.16125 168.36 7.28725C169.176 7.36615 169.576 7.99612 169.576 9.21279V15.7161H172.599V10.8736C172.599 10.3429 172.648 9.94585 172.768 9.54878C173.01 8.43138 173.834 7.28598 175.11 7.28598C176.036 7.28598 176.377 7.80777 176.377 9.21279V14.1126C176.377 15.2198 176.871 15.7161 177.981 15.7161H179.393L179.396 8.71264C179.396 5.91404 178.162 4.49502 175.736 4.49502ZM148.422 11.907C148.413 11.9185 147.123 13.2777 145.421 13.2777C143.872 13.2777 142.307 12.3283 142.307 10.2093C142.307 8.3792 143.52 7.10017 145.258 7.10017C145.822 7.10017 146.464 7.30125 146.565 7.64105L146.579 7.69832C146.643 7.93226 146.782 8.1385 146.976 8.28508C147.17 8.43165 147.406 8.51036 147.649 8.50901L149.293 8.51155V7.07599C149.293 5.18226 146.881 4.49502 145.258 4.49502C141.787 4.49502 139.269 6.90799 139.269 10.2322C139.269 13.5539 141.759 15.9643 145.195 15.9643C148.174 15.9643 149.795 14.0082 149.81 13.9891L149.897 13.8822L148.594 11.725L148.422 11.907Z" fill="#499FDD" />
        </svg>
      </a>
    </div>
    <div class="mb-3 hidden text-center text-sm lg:block">
      Best price guaranteed + Free cancellation
    </div>
    <a
      :href="makeBookingUrl()"
      target="_blank"
      rel="noopener noreferrer"
      class="letter-sp15 mt-2 block h-10 rounded-md bg-blue px-8 py-1 text-center text-sm uppercase leading-8 text-white hover:bg-blue-dark md:h-12 md:text-lg md:leading-10"
      @click="$gtag.event('book-now')"
    >Book now</a>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { mapState } from 'pinia'
import DateRangePicker from '~/components/shared/DateRangePicker'
import Dropdown from '~/components/shared/Dropdown'
import GuestsRoomsSelectors from '~/components/shared/GuestsRoomsSelectors'
import bookingUrl from '~/mixins/booking-url'
import { useVenuesStore } from '~/stores/venues'
import { useCurrenciesStore } from '~/stores/currencies'

dayjs.extend(customParseFormat)

export default {
  components: { GuestsRoomsSelectors, DateRangePicker, Dropdown },

  mixins: [bookingUrl],

  props: {
    venue: { type: Object, required: true },
  },

  data: () => ({
    datesRange: {
      startDate: null,
      endDate: null,
    },

    numGuests: 2,
    numRooms: 1,
    minGuests: 1,
    maxGuests: 20,
    minRooms: 1,
    maxRooms: 10,
    format: 'DD/MM/YYYY',
    minDate: dayjs(),
    showDates: false,
    showRooms: false,
  }),

  computed: {
    ...mapState(useVenuesStore, {
      filters: s => s.filters,
    }),

    ...mapState(useCurrenciesStore, ['currency']),
  },

  watch: {
    datesRange: {
      deep: true,
      handler () {
        this.showDates = false
      },
    },
  },

  mounted () {
    this.datesRange.startDate = this.filters.check_in ? dayjs(this.filters.check_in, this.format).toDate() : null
    this.datesRange.endDate = this.filters.check_out ? dayjs(this.filters.check_out, this.format).toDate() : null
    this.numGuests = this.filters.num_guests || 2
    this.numRooms = this.filters.num_rooms || 1
  },

  methods: {
    makeBookingUrl () {
      const filters = {
        num_rooms: this.numRooms,
      }
      if (this.datesRange.startDate) { filters.check_in = dayjs(this.datesRange.startDate).format(this.format) }
      if (this.datesRange.endDate) { filters.check_out = dayjs(this.datesRange.endDate).format(this.format) }

      return this.bookingUrl(this.venue, filters)
    },
  },
}
</script>
